import {
  GroupRequestCard,
  HangUpPopup,
  LayoutType,
  PedagogyInterventionPage,
  PedagogyInterventionSideBar,
  PedagogyInterventionTopBar,
  PedagogyUtils,
  PlayerSelectionContainer,
  ReactIcons,
  RemoteController,
  RemoteControllerSections,
  UserRequestCard,
} from "@kalyzee/escaet-ui";
import { MeetingSessionOvenMediaReceiverManagerBaseData, useOvenMediaVideoSubscriptions } from "@kalyzee/kast-web-sdk";
import { MeetingState } from "@kalyzee/kast-web-sdk/http";
import { delay, useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import { OvenMediaReceiverWebRTCSession } from "@kalyzee/kast-web-sdk/webrtc";
import { MeetingRoomStatus, MeetingSessionRole, MeetingUpdateByIdResult } from "@kalyzee/kast-web-sdk/websocket";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Outlet } from "react-router-dom";
import DashboardChat from "../../../../../components/dashboard/Chat";
import { useGroupsHighlightRequestsList, useUsersHighlightRequestsList } from "../../../../../components/dashboard/HighlightRequestsList";
import { PageMasterMode, usePageContext } from "../../../../../components/navigation/PageContext";
import { PedagogyBottomBar } from "../../../../../components/pedagogy/BottomBar";
import MeetingSessionMediaPlayer from "../../../../../components/player/MeetingSessionMediaPlayer";
import { ErrorView } from "../../../../../components/utils/ErrorView";
import { LoadingView } from "../../../../../components/utils/LoadingView";
import { ContainerUtils } from "../../../../../helpers/container";
import { closeMeetingActivityRoom, getExpertActor } from "../../../../../helpers/meeting";
import { addPopup, removePopup, removePopupByGroup, removePopupByTag } from "../../../../../helpers/popup";
import { toastError } from "../../../../../helpers/toast";
import { expertWebcamName, pedagogyWebcamName } from "../../../../../helpers/webcam";
import { RestrictedAudioPermission, useMeetingContainerAPI, useMeetingSelector, useMeetingStateUpdater, usePermission } from "../../../../../hooks/meeting";
import { useActivityData, useExpertAvailableViews, usePopulatedMeetingData } from "../../../../../hooks/populatedData";
import { SessionMode } from "../../../../../store/session/slices";
import { usePedagogyContext } from "../../context";
import styles from "./intervention.module.css";
import PauseView from "../../../../../components/utils/PauseView";

const TARGET_ROLE = MeetingSessionRole.SUPERMODERATOR;

const fromUIPage = (page: PedagogyInterventionPage) => {
  const mapping = {
    [PedagogyInterventionPage.Dashboard]: PageMasterMode.MEETING_INTERVENTION_DASHBOARD,
    [PedagogyInterventionPage.Group]: PageMasterMode.MEETING_INTERVENTION_GROUP,
    [PedagogyInterventionPage.Highlight]: PageMasterMode.MEETING_INTERVENTION_HIGHLIGHT,
    [PedagogyInterventionPage.Survey]: PageMasterMode.MEETING_INTERVENTION_SURVEY,
  };
  return mapping[page];
};

const toUIPage = (page?: PageMasterMode) => {
  if (page === PageMasterMode.MEETING_INTERVENTION_DASHBOARD) return PedagogyInterventionPage.Dashboard;
  if (page === PageMasterMode.MEETING_INTERVENTION_GROUP) return PedagogyInterventionPage.Group;
  if (page === PageMasterMode.MEETING_INTERVENTION_HIGHLIGHT) return PedagogyInterventionPage.Highlight;
  if (page === PageMasterMode.MEETING_INTERVENTION_SURVEY) return PedagogyInterventionPage.Survey;
  return PedagogyInterventionPage.Dashboard;
};

export const MeetingPedagogyInterventionPage = () => {
  const pageContext = usePageContext<SessionMode.MASTER>();
  const pageContextRef = useRefAutoUpdate(pageContext);
  const pedagogyCtx = usePedagogyContext();
  const pedagogyCtxRef = useRefAutoUpdate(pedagogyCtx);

  const meetingContainerAPI = useMeetingContainerAPI(pageContextRef.current?.params?.id);
  const meetingContainerAPIRef = useRefAutoUpdate(meetingContainerAPI);
  const [meeting] = useMeetingSelector(meetingContainerAPI, (state) => state.meeting);
  const [rooms] = useMeetingSelector(meetingContainerAPI, (state) => state.rooms);
  const [session, sessionRef] = useMeetingSelector(meetingContainerAPI, (state) => state.session);
  const [surveys] = useMeetingSelector(meetingContainerAPI, (state) => state.surveys);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [remoteControllerState, setRemoteControllerState] = useState<{ interventions: "closed" | "opened"; chat: "closed" | "opened" }>({
    interventions: "closed",
    chat: "closed",
  });
  const [error, setError] = useState(false);
  const updatingRoleRef = useRef(false);
  const usersHighlightRequestsList = useUsersHighlightRequestsList(meetingContainerAPI);
  const groupsHighlightRequestsList = useGroupsHighlightRequestsList(meetingContainerAPI);
  const permissionAPI = usePermission(meeting);

  const populatedMeetingData = usePopulatedMeetingData(meetingContainerAPI);
  const populatedMeetingDataRef = useRefAutoUpdate(populatedMeetingData);

  const activityData = useActivityData(populatedMeetingData);
  const availableViews = useExpertAvailableViews(populatedMeetingData, activityData, pedagogyCtx?.spiedGroupId);
  const currentView = useMemo(() => {
    const highlightView = availableViews.find((view) => view.type === "highlight");
    if (highlightView) return highlightView;
    return availableViews[availableViews.length - 1]; // TODO : there should be a default view, like this structure : { availableViews, defaultView, currentView }
  }, [availableViews]);

  const subscribptionsAPI = useOvenMediaVideoSubscriptions();

  const expertNonWebcamVideoSessions = useMemo(
    () =>
      (currentView?.experts ?? [])
        .flatMap((data) => data.session.videoSessions)
        .filter((videoSession) => videoSession.data.mediaSession.name !== expertWebcamName),
    [currentView]
  );

  const exitingRef = useRef(false);

  const lastPages = useRef<PedagogyInterventionPage[]>([]);
  const pages = useMemo(() => {
    const newPages: PedagogyInterventionPage[] = [PedagogyInterventionPage.Dashboard];
    if (
      availableViews.find((v) =>
        ["activity_all_leaders", "activity_one_group", "activity_one_group_spy", "activity_one_leader", "groups_overview"].includes(v.type)
      )
    ) {
      newPages.push(PedagogyInterventionPage.Group);
    }
    if (availableViews.find((v) => v.type === "highlight")) {
      newPages.push(PedagogyInterventionPage.Highlight);
    }
    if (surveys?.length) {
      newPages.push(PedagogyInterventionPage.Survey);
    }
    return newPages;
  }, [availableViews, surveys]);

  useEffect(() => {
    return () => {
      lastPages.current = pages;
    };
  }, [pages]);

  const ongoingGroups = useMemo(
    () => (populatedMeetingData?.rooms?.groups?.filter((g) => g.room.status === MeetingRoomStatus.Ongoing)?.length ? true : false),
    [populatedMeetingData]
  );

  const { update: updateMeetingState } = useMeetingStateUpdater(meetingContainerAPI);

  // ---------------------------------------------------------------------- //
  // ---------------------------   UTILS ----------------------------- //
  // ---------------------------------------------------------------------- //

  const stop = async (): Promise<boolean> => {
    pageContextRef.current?.setLoading(true);
    await delay(200);
    const result = await updateMeetingState(MeetingState.STOPPED);
    pageContextRef.current?.setLoading(false);
    if (result && !result.error && result.response?.meeting?.state === MeetingState.STOPPED) {
      pageContextRef.current?.goTo(PageMasterMode.DASHBOARD);
      return true;
    }
    toastError("Une erreur est apparue.");
    return false;
  };

  const updateSessionRole = async () => {
    if (updatingRoleRef.current || exitingRef.current) return;
    if (!session || !meetingContainerAPI) {
      setError(true);
      return false;
    }
    updatingRoleRef.current = true;
    setError(false);
    try {
      const type = "camera";
      const group = "cameras";
      const response = await meetingContainerAPI.updater.update(session.id, {
        role: TARGET_ROLE,
        medias: [
          {
            video: {
              deviceType: "camera",
              enabled: false, // disabled by default
            },
            audio: {
              deviceType: "audio",
              enabled: false, // disabled by default
            },
            name: pedagogyWebcamName,
            extra: {
              type,
            },
          },
        ],
      });
      if (!response?.response || response.error) {
        setError(true);
      }
    } catch (err) {
      setError(true);
    } finally {
      updatingRoleRef.current = false;
    }
  };

  const exit = async () => {
    if (!session || !meetingContainerAPI || exitingRef.current) {
      return;
    }
    exitingRef.current = true;
    pageContextRef.current?.setLoading(true);
    let success = false;
    try {
      const response = await meetingContainerAPIRef.current?.updater.update(session.id, { role: MeetingSessionRole.SUPERSPECTATOR, medias: [] });
      if (response?.response && !response?.error) {
        success = true;
      }
      if (sessionRef.current?.canBeHeardByEveryone) {
        await ContainerUtils.setMeetingSessionHeardByEveryoneState(meetingContainerAPI, session.id, false);
      }
      if (activityData?.mode === "pedagogy" && meeting) {
        // Expert to pedagogy mode
        await closeMeetingActivityRoom(meeting.id);
      }
    } catch (err) {
      console.error(err);
    }
    pageContextRef.current?.setLoading(false);
    if (!success) {
      exitingRef.current = false;
      toastError("Une erreur est apparue");
      return;
    }
    pageContextRef.current?.goTo(PageMasterMode.MEETING_DASHBOARD, pageContextRef.current.params);
  };

  const getLabel = useCallback((session: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData<any>>) => {
    return session.data.mediaSession.extra?.label ?? "Caméra";
  }, []);

  const onPageChange = useCallback((page: PedagogyInterventionPage) => {
    pageContextRef.current?.goTo(fromUIPage(page), { id: pageContextRef.current?.params?.id });
  }, []);

  const talkingToExpert = pageContextRef.current?.page === PageMasterMode.MEETING_INTERVENTION_TALK_TO_EXPERT;

  // ---------------------------------------------------------------------- //
  // ---------------------------   EFFECT ----------------------------- //
  // ---------------------------------------------------------------------- //

  useEffect(() => {
    subscribptionsAPI.update(expertNonWebcamVideoSessions.map((s) => ({ session: s, active: true })));
    return () => subscribptionsAPI.update(expertNonWebcamVideoSessions.map((s) => ({ session: s, active: false })));
  }, [subscribptionsAPI, expertNonWebcamVideoSessions]);

  useEffect(() => {
    const beforeGoToListener = pageContextRef.current?.addEventListener("onBeforeGoto", () => {
      removePopupByGroup("main"); // Clean popup asap
    });
    pedagogyCtxRef.current?.setDisplayRemoteController(false); // hide by default
    return () => {
      removePopupByGroup("main");
      beforeGoToListener?.flush();
      exit();
    };
  }, []);

  useEffect(() => {
    if (!session) return;
    if (session.role !== TARGET_ROLE || !session.medias?.length) {
      updateSessionRole();
    }
  }, [session]);

  useEffect(() => {
    // Not ready to use, we cannot decide if a redirection is needed
    if (!meeting) return;
    if (!pageContextRef.current?.page?.startsWith(PageMasterMode.MEETING_INTERVENTION) || pageContextRef.current?.page === PageMasterMode.MEETING_INTERVENTION)
      return;
    if (pageContextRef.current?.page === PageMasterMode.MEETING_INTERVENTION_TALK_TO_EXPERT) return;
    if (pageContextRef.current?.page === PageMasterMode.MEETING_INTERVENTION_SURVEY) return;

    // Priority to highllighted content
    if (pages.find((page) => page === PedagogyInterventionPage.Highlight)) {
      const alreadyIn = lastPages.current.find((page) => page === PedagogyInterventionPage.Highlight);
      if (alreadyIn) {
        // We already had highlighted sessions (or were highlighted ourself) before this update
        // Maybe we are on the hightlight page, maybe not, does not matter
        return;
      }
      pageContextRef.current?.goTo(PageMasterMode.MEETING_INTERVENTION_HIGHLIGHT, pageContextRef.current?.params);
      return;
    }

    if (pages.find((page) => page === PedagogyInterventionPage.Group)) {
      const alreadyIn = lastPages.current.find((page) => page === PedagogyInterventionPage.Group);
      if (alreadyIn) {
        return;
      }
      pageContextRef.current?.goTo(PageMasterMode.MEETING_INTERVENTION_GROUP, pageContextRef.current?.params);
      return;
    }

    // Nothing is highlighted, nothing is active, we can go back to the dashboard
    if (
      pageContextRef.current?.page !== PageMasterMode.MEETING_INTERVENTION_DASHBOARD &&
      pageContextRef.current?.page !== PageMasterMode.MEETING_INTERVENTION_GROUP
    ) {
      pageContextRef.current?.goTo(PageMasterMode.MEETING_INTERVENTION_DASHBOARD, pageContextRef.current?.params);
    }
  }, [pages, pageContextRef, populatedMeetingData, surveys]);

  // TODO : temp fix ... this should be a backend fix
  // if something goes wrong here, the peda will still appear to everyone after groups are closed
  // and we go back to normal mode
  useEffect(() => {
    const isInGroups = pedagogyCtx?.spiedGroupId || session?.canBeHeardByEveryone || ["activity_one_group", "activity_one_group_spy", "activity_one_leader", "activity_all_leaders"].includes(currentView.type);
    const allClosed = populatedMeetingData?.rooms?.groups?.every((g) => g.room.status !== MeetingRoomStatus.Ongoing);
    if (allClosed && isInGroups) {
      exit();
    }
  }, [currentView, session, populatedMeetingData, pedagogyCtx]);

  // ---------------------------------------------------------------------- //
  // ---------------------------   RENDER ----------------------------- //
  // ---------------------------------------------------------------------- //

  if (error)
    return (
      <ErrorView
        retry={() => {
          updateSessionRole();
        }}
      />
    );
  if (!session || session.role !== TARGET_ROLE || !meeting) return <LoadingView />;

  const renderHighlightBanner = () => {
    const highlightView = availableViews.find((view) => view.type === "highlight");

    if (!highlightView || !session) {
      return null;
    }

    const mySessionData = highlightView.students.find((s) => s.video && s.session.id === session.id);

    let otherGroupsNames = highlightView.students
      .filter(
        (data) =>
          data.video &&
          data.session.user.group?.room.status === MeetingRoomStatus.Ongoing &&
          data.session.user.group.id !== mySessionData?.session.user.group?.id
      )
      .map((data) => data.session.user.group?.room.name);
    otherGroupsNames = Array.from(new Set(otherGroupsNames));

    let you = false;
    let plural = false;

    let message = "";

    if (mySessionData) {
      if (mySessionData.session.user.group?.room.status === MeetingRoomStatus.Ongoing) {
        message = "Votre groupe";
      } else {
        message = "Vous";
        you = true;
      }
    }

    /**
     * Either :
     * - there is atleast one group and we display group names only, ignoring users
     * - there is no group and we display the users number
     */
    if (otherGroupsNames.length === 1) {
      if (message) {
        message += " et le groupe ";
        plural = true;
      } else {
        message = "Le groupe ";
      }
      message += otherGroupsNames[0];
    } else if (otherGroupsNames.length > 1) {
      if (message) {
        message += " et les groupes ";
      } else {
        message = "Les groupes ";
      }
      message += otherGroupsNames.join(", ");
    } else {
      // no other groups

      // users that are not in the same room as me
      const otherSessions = highlightView.students.filter(
        (s) =>
          s.video && s.session.id !== mySessionData?.session.id && (!s.session.user.group || s.session.user.group?.id !== mySessionData?.session.user.group?.id)
      );
      const remaining = otherSessions.length;

      if (remaining === 1) {
        if (message) {
          message += " et 1 autre utilisateur";
          plural = true;
        } else {
          message = "1 utilisateur";
        }
      } else if (remaining > 1) {
        plural = true;
        if (message) {
          message += ` et ${remaining} autres utilisateurs`;
        } else {
          message = `${remaining} utilisateurs`;
        }
      }
    }

    if (message) {
      if (you) {
        message += " êtes au tableau";
      } else {
        if (plural) {
          message += " sont au tableau";
        } else {
          message += " est au tableau";
        }
      }
    } else {
      message = "Préparation du tableau ...";
    }

    const isOnHighlightPage = pageContextRef.current?.page === PageMasterMode.MEETING_INTERVENTION_HIGHLIGHT;

    return (
      <div
        onClick={() => {
          if (!isOnHighlightPage) {
            pageContextRef.current?.goTo(PageMasterMode.MEETING_INTERVENTION_HIGHLIGHT, { id: pageContextRef.current?.params?.id });
          }
        }}
        style={{
          width: "100%",
          display: "flex",
          gap: "1em",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          backgroundColor: "#0090FF",
          opacity: isOnHighlightPage ? 1 : 0.7,
          cursor: isOnHighlightPage ? "default" : "pointer",
          padding: "0.5em",
        }}
      >
        <div
          style={{
            color: "white",
            fontWeight: "bold",
          }}
        >
          {message.toUpperCase()}
        </div>
      </div>
    );
  };

  const renderRemoteController = () => {
    const checked = !permissionAPI.hasPermission("audio", RestrictedAudioPermission);
    const chatroom = meeting.chatrooms?.[0];
    if (talkingToExpert) return null;
    return (
      <RemoteController
        style={{ display: pedagogyCtx?.displayRemoteController ? "inherit" : "none", height: "calc(100% - 10px)", marginRight: "5px", width: 400, zoom: 0.9 }}
        stylePiP={{ zoom: 1 }}
        footerElements={[
          !ongoingGroups ? (
            <PedagogyUtils.EnableMicrophone
              dark
              style={{ width: "100%" }}
              key={"enable-microphone"}
              isMicrophoneActive={checked}
              onMicrophoneStatusChange={() => permissionAPI.setPermission("audio", RestrictedAudioPermission, checked)}
              disabled={ongoingGroups}
            />
          ) : null,
          /*<PedagogyUtils.SoundSlider
            key={"volume-slider"}
            onVolumeChange={(e: any) => {
              const volume = Number(e.target.value);
              // set volume
            }}
            onMuteVolume={() => {
              // set 0
            }}
            onMaximumVolume={() => {
              // set 100
            }}
            onFocus={() => {}}
            isDark={true}
            volume={"50"}
            style={{ width: "100%" }}
          />,*/
          <div key={"buttons-container"} style={{ width: "100%", display: "flex", gap: 5, alignItems: "center" }}>
            <PedagogyUtils.HangUpButton
              dark
              style={{ flex: 1, minWidth: 0 }}
              onClick={() => {
                window.focus();
                removePopupByTag("meeting-hangup-popup");
                const start = meeting?.startedAt ? new Date(meeting?.startedAt) : undefined;
                const overlay = (
                  <HangUpPopup
                    startCourseDate={start}
                    onNext={async () => {
                      removePopup(overlay);
                      await stop();
                    }}
                    onClose={() => {
                      removePopup(overlay);
                    }}
                    onPerformExitCall={() => {
                      removePopup(overlay);
                      pageContextRef.current?.goTo(PageMasterMode.MEETING_CALL, { id: meeting?.id }, { queries: { exit: "true" }});
                    }}
                  />
                );
                addPopup(overlay, "meeting-hangup-popup", "main");
              }}
            />
            {meeting.state === MeetingState.PAUSED ? (
              <PedagogyUtils.PlayButton
                date={new Date(meeting?.pausedAt ?? new Date())}
                dark
                style={{ flex: 1, minWidth: 0 }}
                onClick={() => updateMeetingState(MeetingState.RUNNING)}
              />
            ) : (
              <PedagogyUtils.PauseButton
                date={new Date(meeting?.startedAt ?? new Date())}
                dark
                style={{ flex: 1, minWidth: 0 }}
                onClick={() => updateMeetingState(MeetingState.PAUSED)}
              />
            )}
          </div>,
        ]}
      >
        {chatroom ? (
          <RemoteControllerSections.Chat
            defaultOpened={remoteControllerState.chat === "opened"}
            disableUnmountContent
            badge={unreadMessages ? `${unreadMessages}` : undefined}
            onOpen={(v: boolean) => setRemoteControllerState((s) => ({ ...s, chat: v ? "opened" : "closed" }))}
          >
            <DashboardChat
              chatroom={chatroom}
              inactive={remoteControllerState.chat === "closed"}
              hideHeader
              meetingContainer={meetingContainerAPI}
              style={{ width: "100%", height: "40vh" }}
              onUnreadMessages={(messages) => {
                setUnreadMessages(messages.length);
              }}
              disableDeletion={false}
            />
          </RemoteControllerSections.Chat>
        ) : null}
        {ongoingGroups ? (
          <RemoteControllerSections.Intervention
            defaultOpened={remoteControllerState.interventions === "opened"}
            disableUnmountContent
            badge={groupsHighlightRequestsList.groups.length ? `${groupsHighlightRequestsList.groups.length}` : undefined}
            onOpen={(v: boolean) => setRemoteControllerState((s) => ({ ...s, interventions: v ? "opened" : "closed" }))}
          >
            <div className={styles.interventionContainer}>
              <div className={styles.interventionContent}>
                {groupsHighlightRequestsList.groups.map((group, index) => (
                  <GroupRequestCard
                    key={index}
                    name={`Groupe ${group.room.name ?? ""}`}
                    onAccept={() => groupsHighlightRequestsList.respond(group.room, true)}
                    onCancel={() => groupsHighlightRequestsList.respond(group.room, false)}
                  />
                ))}
              </div>
              <div className={styles.interventionButtonsConainer}>
                <PedagogyUtils.InterventionRefuseAllButton
                  style={{ fontSize: "13px !important" }}
                  numberBadge={`${groupsHighlightRequestsList.groups.length}`}
                  onClick={() => groupsHighlightRequestsList.respondAll(false)}
                />
                <PedagogyUtils.InterventionAcceptAllButton
                  style={{ fontSize: "13px !important" }}
                  numberBadge={`${groupsHighlightRequestsList.groups.length}`}
                  onClick={() => groupsHighlightRequestsList.respondAll(true)}
                />
              </div>
            </div>
          </RemoteControllerSections.Intervention>
        ) : (
          <RemoteControllerSections.Intervention
            defaultOpened={remoteControllerState.interventions === "opened"}
            disableUnmountContent
            badge={usersHighlightRequestsList.users.length ? `${usersHighlightRequestsList.users.length}` : undefined}
            onOpen={(v: boolean) => setRemoteControllerState((s) => ({ ...s, interventions: v ? "opened" : "closed" }))}
          >
            <div className={styles.interventionContainer}>
              <div className={styles.interventionContent}>
                {usersHighlightRequestsList.users.map((user, index) => (
                  <UserRequestCard
                    key={index}
                    profilePicture={user.profilePicture}
                    firstName={user.firstName}
                    lastName={user.lastName}
                    onAccept={() => usersHighlightRequestsList.respond(user.session, true)}
                    onCancel={() => usersHighlightRequestsList.respond(user.session, false)}
                  />
                ))}
              </div>
              <div className={styles.interventionButtonsConainer}>
                <PedagogyUtils.InterventionRefuseAllButton
                  style={{ fontSize: "13px !important" }}
                  numberBadge={`${usersHighlightRequestsList.users.length}`}
                  onClick={() => usersHighlightRequestsList.respondAll(false)}
                />
                <PedagogyUtils.InterventionAcceptAllButton
                  style={{ fontSize: "13px !important" }}
                  numberBadge={`${usersHighlightRequestsList.users.length}`}
                  onClick={() => usersHighlightRequestsList.respondAll(true)}
                />
              </div>
            </div>
          </RemoteControllerSections.Intervention>
        )}
        <div
          style={{
            width: "100%",
            display: "flex",
            gap: 5,
          }}
        >
          <PedagogyUtils.SurveyButton
            style={{ flex: 1 }}
            onClick={() => {
              pageContextRef.current?.goTo(PageMasterMode.MEETING_INTERVENTION_SURVEY, pageContextRef.current.params);
            }}
          />
          <PedagogyUtils.GroupButton
            style={{ flex: 1 }}
            onClick={() => {
              pageContextRef.current?.goTo(PageMasterMode.MEETING_INTERVENTION_GROUP, pageContextRef.current.params);
            }}
          />
        </div>
      </RemoteController>
    );
  };

  const renderHeader = () => {
    if (!meeting) return null;
    if (pageContext?.page === PageMasterMode.MEETING_INTERVENTION_TALK_TO_EXPERT) {
      return (
        <PedagogyInterventionTopBar
          title={
            <div className={styles.expertTopBarContent}>
              <ReactIcons.HeadphonePresence color={"#FFFFFF88"} width={24} height={24} style={{ marginRight: 10 }} />
              ÉCHANGER AVEC L'EXPERT
              {meeting.room?.name ? <span>{`(SALLE ${meeting.room?.name})`}</span> : null}
            </div>
          }
          forceOpen
          style={{ height: 60 }}
        />
      );
    }
    const expertUser = getExpertActor(meeting)?.user;
    const splitted = meeting?.name?.split("-") ?? [];
    const courseName = splitted.length > 1 ? splitted[0].trim() : "";
    return (
      <>
        <PedagogyInterventionTopBar
          title={meeting?.name ?? ""}
          date={new Date(meeting?.startedAt ?? new Date())}
          userProfileData={{
            courseName,
            firstName: expertUser?.firstname ?? "",
            lastName: expertUser?.lastname ?? "",
            profilePicture: expertUser?.avatar,
          }}
        />
        <div style={{ paddingTop: 32 }} />
      </>
    );
  };

  const renderContent = () => {
    const renderSideBar = () => {
      if (pageContext?.page === PageMasterMode.MEETING_INTERVENTION_TALK_TO_EXPERT) return null;
      const renderSideBarPlayer = (session: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData>) => {
        const mediaSessionId = session.data.mediaSession.id;
        const isDisabled = pedagogyCtx?.disabledExpertMediaSessionIds?.find((id) => id === session.data.mediaSession?.id);
        return (
          <PlayerSelectionContainer
            key={session.getId()}
            enabled={!isDisabled}
            onEnable={(v: boolean) => {
              const mediaSessionId = session.data.mediaSession?.id;
              if (!mediaSessionId) return;
              if (v) pedagogyCtxRef.current?.enableExpertMediaSessionId(mediaSessionId);
              else pedagogyCtxRef.current?.disableExpertMediaSessionId(mediaSessionId);
            }}
            label={getLabel(session)}
            style={{ width: "100%" }}
          >
            <MeetingSessionMediaPlayer
              style={{ width: "100%", height: "150px" }}
              meeting={meeting}
              sessionId={session.data.session.id}
              mediaSessionId={mediaSessionId}
              ovenMediaSession={session}
              muted={true}
            />
          </PlayerSelectionContainer>
        );
      };

      return (
        <PedagogyInterventionSideBar
          screens={expertNonWebcamVideoSessions.map((s) => renderSideBarPlayer(s))}
          selectedLayout={pedagogyCtx?.layout ?? LayoutType.Square}
          enabledLayouts={[LayoutType.Square, LayoutType.HorizontalSquare, LayoutType.VerticalSquare]}
          onLayoutChange={(layout: LayoutType) => pedagogyCtx?.setLayout(layout)}
          selectedPage={toUIPage(pageContextRef.current?.page)}
          enabledPages={pages}
          onPageChange={onPageChange}
        />
      );
    };
    return (
      <div className={styles.contentContainer}>
        {renderSideBar()}
        <div className={styles.content}>
          <Outlet />
        </div>
        {renderRemoteController()}
      </div>
    );
  };

  const renderFooter = () => (
    <PedagogyBottomBar
      meetingContainerAPI={meetingContainerAPI}
      context={pedagogyCtx}
      backButtonText="Sortir de la salle"
      onBackButton={exit}
      onRemoteController={() => {
        if (!pedagogyCtx) return;
        pedagogyCtx.setDisplayRemoteController(!pedagogyCtx.displayRemoteController);
      }}
      remoteBadgeValue={(ongoingGroups ? groupsHighlightRequestsList.groups.length : usersHighlightRequestsList.users.length) + unreadMessages}
      disableRemoteButton={talkingToExpert}
    />
  );

  return (
    <div className={styles.container}>
      {renderHeader()}
      {renderHighlightBanner()}
      {renderContent()}
      {renderFooter()}
      {meeting?.state === MeetingState.PAUSED ? (
        <PauseView
          mode="pedagogy"
          meeting={meeting}
          back={{
            text: "Sortir du cours",
            onClick: () => {
              exit();
            }
          }}
          onResume={async () => {
            await updateMeetingState(MeetingState.RUNNING);
          }}
          onStop={async () => {
            await stop();
          }}
        />
      ) : null}
    </div>
  );
};
